<template>
    <page-container class="vx-grid vx-grid-cols-12 vx-gap-8">
        <settings-sidebar></settings-sidebar>
        <div class="vx-col-span-12 md:vx-col-span-8 lg:vx-col-span-10">
            <page-header>
                Promotions

                <template v-slot:actions>
                    <vx-button
                        @click="showOverview = !showOverview"
                        data-test="toggle-overview"
                        type="button"
                        color="muted-dark"
                        size="lg"
                    >
                        <span class="vx-hidden sm:vx-inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                        <font-awesome-icon
                            class="vx-hidden sm:vx-inline-block"
                            :icon="faQuestionCircle"
                        ></font-awesome-icon>
                        <font-awesome-icon
                            class="vx-inline-block sm:vx-hidden"
                            :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                        ></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <promotions-overview v-show="showOverview" class="vx-mb-10"></promotions-overview>

            <promotion-controls
                v-if="list.length || !isEqual(defaultFilters(), fetchParams)"
                v-model="fetchParams"
                @reset="resetFilters"
                @apply="applyFilters"
                class="vx-mb-8"
            ></promotion-controls>

            <page-loading v-if="loading && !list.length"></page-loading>

            <vx-box
                v-if="!loading && !list.length"
                color="info"
                class="vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500"
            >
                No promotions yet.
                <vx-button href="/settings/integrations/promotions/new">
                    New Promotion <font-awesome-icon :icon="faPlusCircle" />
                </vx-button>
            </vx-box>

            <vx-table v-if="list.length" class="lg:vx-grid-cols-6-auto vx-mt-4" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>ID</div>
                    <div class="xl:vx-col-span-2">Name</div>
                    <div>Allocation</div>
                    <div>Created at</div>
                </vx-table-row>

                <promotion-row
                    v-for="(item, index) in list"
                    :modelValue="list[index]"
                    :key="list[index].id"
                    @deleted="removeFromList($event)"
                ></promotion-row>
            </vx-table>

            <div v-if="fetchParams.cursor" class="vx-mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="lg"
                    class="vx-grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faPlusCircle,
    faQuestionCircle,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import { onMounted, ref } from 'vue';
import PromotionsOverview from '~/components/promotions/PromotionsOverview.vue';
import PromotionControls from '~/components/promotions/PromotionControls.vue';
import PromotionRow from '~/components/promotions/PromotionRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { promotionsClient } from '~/services/index';
import PageLoading from '~/components/general/PageLoading.vue';
import SettingsSidebar from '~/components/settings/menus/SettingsSidebar.vue';
import { isEqual } from '~/utils/helpers';

const showOverview = ref(true);

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    query: '',
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    return fetchRecords(promotionsClient.getAll(Spark.state.currentTeam.id, { ...fetchParams.value }));
};

const removeFromList = (promotionId) => {
    list.value = list.value.filter((item) => item.id != promotionId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
